import React, { use, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Button, Table, Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { receiptNoteActions } from '../../sagas/receiptNoteSlice';
import Swal from 'sweetalert2';
import { formatPrice } from '../../helpers/utility';
import { entrepotActions } from '../../sagas/entrepotSlice';
import Select from "react-select";
import { da } from 'date-fns/locale';

const ReceiptNoteForm = ({ initialValues, onClose, purchaseOrder }) => {
    const { t } = useTranslation();
    const [receiptNote, setReceiptNote] = useState(initialValues);
    const dispatch = useDispatch();
    const { entrepots, loading } = useSelector(state => state.entrepots);

    useEffect(() => {
        dispatch(entrepotActions.list());
    }, [])


    const entrepotOptions = (entrepots.filter(e => e.libelle !== 'EXTERNE' && e.libelle !== 'RETOUR') ?? [])?.map(entrepot => ({
        value: entrepot.id,
        label: entrepot.code + ' - ' + entrepot.libelle,
    })) ?? [];

    const handleInputChange = (index, event) => {
        const { name, value } = event.target;
        const newLines = [...receiptNote.lignes];
        const updatedLine = { ...newLines[index], [name]: value };
        newLines[index] = updatedLine;
        const receivedQuantity = parseFloat(value) || 0;
        const prixUnitaireHT = parseFloat(newLines[index].prixUnitaireHT) || 0;
        const tva = parseFloat(newLines[index].tva) || 0;

        const totalHt = receivedQuantity * prixUnitaireHT;
        const totalTtc = totalHt + (totalHt * tva);

        newLines[index].montantHT = totalHt.toFixed(2);
        newLines[index].montantTTC = totalTtc.toFixed(2);

        const totalHtSum = newLines.reduce((acc, line) => acc + parseFloat(line.montantHT), 0);
        const totalTtcSum = newLines.reduce((acc, line) => acc + parseFloat(line.montantTTC), 0);
        const totalTvaSum = newLines.reduce((acc, line) => acc + parseFloat(line.montantTTC - line.montantHT), 0);

        setReceiptNote({
            ...receiptNote,
            lignes: newLines,
            montantTotalHT: totalHtSum.toFixed(2),
            montantTotalTTC: totalTtcSum.toFixed(2),
            montantTotalTaxe: totalTvaSum.toFixed(2)
        });

    };

    const handleSubmit = (e) => {
        e.preventDefault(); // Prevent default form submission

        if (!receiptNote.deliveryNoteNb) {
            Swal.fire({
                icon: 'warning',
                title: t('reception.missingDeliveryNoteTitle', 'Missing Delivery Note Number'),
                text: t('reception.missingDeliveryNoteMessage', 'Please enter a delivery note number before submitting.'),
                confirmButtonText: t('common.ok', 'OK')
            });
            return; // Stop the submission process
        }

        if(!receiptNote.entrepotId){
            Swal.fire({
                icon: 'warning',
                title: t('reception.missingEntrepotTitle', 'Missing Warehouse'),
                text: t('reception.missingEntrepotMessage', 'Please select a warehouse before submitting.'),
                confirmButtonText: t('common.ok', 'OK')
            });
            return; // Stop the submission process
        }


        Swal.fire({
            title: t('common.confirm'),
            text: t('reception.confirmSaveMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('common.save'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                // TODO: Implement save logic here
                console.log('Receipt Note Data:', receiptNote);
                if (receiptNote.id > 0) {
                    dispatch(receiptNoteActions.updateReceiptNote({
                        id: receiptNote.id,
                        data: receiptNote,
                        onSuccess: () => {
                            Swal.fire(
                                t('common.saved'),
                                t('reception.savedSuccess'),
                                'success'
                            );
                            onClose();
                        },
                        onError: (error) => {
                            Swal.fire(
                                t('common.error'),
                                t('reception.saveError'),
                                'error'
                            );
                        }
                    }));
                } else {
                    dispatch(receiptNoteActions.createReceiptNote({
                        data: receiptNote,
                        onSuccess: () => {
                            Swal.fire(
                                t('common.saved'),
                                t('reception.savedSuccess'),
                                'success'
                            );
                            onClose();
                        },
                        onError: (error) => {
                            Swal.fire(
                                t('common.error'),
                                t('reception.saveError'),
                                'error'
                            );
                        }
                    }));
                }
            }
        });
    };

    const handleValidate = () => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reception.confirmValidateMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('reception.confirmValidate'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                if (receiptNote.id > 0) {
                    dispatch(receiptNoteActions.validateReceiptNote({
                        id: receiptNote.id,
                        onSuccess: () => {
                            Swal.fire(
                                t('common.saved'),
                                t('reception.savedSuccess'),
                                'success'
                            );
                            onClose();
                        },
                        onError: (error) => {
                            Swal.fire(
                                t('common.error'),
                                t('reception.saveError'),
                                'error'
                            );
                        }
                    }));
                }
            }
        });
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md="3">
                    <FormGroup>
                        <Label>{t('reception.purchaseOrderNumber')}</Label>
                        <Input type="text" value={purchaseOrder.numero} disabled />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <Label>{t('reception.supplier')}</Label>
                        <Input type="text" value={receiptNote.fournisseurNom} disabled />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <Label htmlFor="entrepotId">{t('bonLivraison.entrepot')}:</Label>
                    <Select
                        id="entrepotId"
                        options={entrepotOptions}
                        value={entrepotOptions.find(option => option.value === receiptNote.entrepotId)}
                        onChange={(selectedOption) => setReceiptNote({ ...receiptNote, entrepotId: selectedOption.value })}
                        placeholder={t('bonLivraison.selectEntrepot')}
                        isSearchable
                        isDisabled={receiptNote && receiptNote.status === 'VALIDE' ? true : false}
                    />

                </Col>
                <Col md="3">
                    <FormGroup>
                        <Label>{t('reception.deliveryNoteNb')}</Label>
                        <Input
                            type="text"
                            name="deliveryNoteNb"
                            value={receiptNote.deliveryNoteNb || ''}
                            onChange={(e) => setReceiptNote({ ...receiptNote, deliveryNoteNb: e.target.value })}
                            disabled={receiptNote.statut === 'VALIDE'}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>

                <Col md="3">
                    <FormGroup>
                        <Label>{t('reception.receiptDate')}</Label>
                        <Input
                            type="date"
                            name="receiptDate"
                            value={receiptNote.dateReception || ''}
                            onChange={(e) => setReceiptNote({ ...receiptNote, dateReception: e.target.value })}
                            disabled={receiptNote.statut === 'VALIDE'}
                        />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <Label htmlFor="dateOperation">{t('commande.dateOperation')}:</Label>
                    <Input
                        type="date"
                        value={receiptNote.dateOperation || ''}
                        onChange={(e) => setReceiptNote({ ...receiptNote, dateOperation: e.target.value })}
                        className="form-control"
                        disabled={receiptNote.statut === 'VALIDE'}
                    />
                </Col>
            </Row>
            <Table>
                <thead>
                    <tr>
                        <th>{t('reception.productDescription')}</th>
                        {receiptNote.commandeId && <th>{t('reception.orderedQuantity')}</th>}

                        <th>{t('reception.receivedQuantity')}</th>
                        <th>{t('reception.prixUnitaireHT')}</th>
                        <th>{t('reception.totalHt')}</th>
                        <th>{t('reception.tva')}</th>
                        <th>{t('reception.totalTtc')}</th>
                    </tr>
                </thead>
                <tbody>
                    {receiptNote.lignes && receiptNote.lignes.map((line, index) => (
                        <tr key={index}>
                            <td>{line.articleNom}</td>
                            {receiptNote.commandeId && <td>{line.quantiteCommandee}</td>}
                            <td>
                                <Input
                                    type="number"
                                    onFocus={(e) => e.target.select()} // Select the field content on focus
                                    name="quantiteRecue"
                                    value={line.quantiteRecue}
                                    onChange={(e) => handleInputChange(index, e)}
                                    disabled={receiptNote.statut === 'VALIDE'}
                                />
                            </td>
                            <td>{line.prixUnitaireHT}</td>
                            <td>{line.montantHT}</td>
                            <td>{line.tauxTVA * 100} %</td>
                            <td>{line.montantTTC}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <Row className="mt-3 justify-content-end">
                <Col md={4}>
                    <div className="totals text-center">
                        <p><strong>{t('cart.totalHT')}:</strong> {formatPrice(receiptNote.montantTotalHT)}</p>
                        <p><strong>{t('cart.totalTVA')}:</strong> {formatPrice(receiptNote.montantTotalTaxe)}</p>
                        <p><strong>{t('cart.totalTTC')}:</strong> {formatPrice(receiptNote.montantTotalTTC)}</p>

                    </div>
                </Col>
            </Row>
            <Button color="primary" type="submit" className="me-2" disabled={receiptNote.statut === 'VALIDE'}
            >{t('common.save')}</Button>
            {receiptNote.id > 0 && receiptNote.statut === 'EN_ATTENTE' && (
                <Button color="success" type="button" className="me-2" onClick={handleValidate}>{t('reception.validateReceiptNote')}</Button>
            )}
            <Button color="secondary" onClick={onClose}>{t('common.cancel')}</Button>
        </Form>
    );
};

export default ReceiptNoteForm;